import { Spinner } from '@/components/glitch-ui/spinner';
import React, { useCallback, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { conversionTrackingQueryOptions } from '../lib/query-options';
import { CheckCircle, AlertCircle, ExternalLink } from 'lucide-react';
import { useAtomValue } from 'jotai';
import { googleAdsAccountAtom } from '@/store';
import { motion } from 'framer-motion';

interface TrackingIssuesProps {
  onNext: () => void;
  setStepValid: (valid: boolean) => void;
  setIsWarningErrorSuccess: (isWarning: boolean) => void;
}

const CONVERSION_TRACKING_MANAGED_STATUS = [
  'CONVERSION_TRACKING_MANAGED_BY_SELF',
  'CONVERSION_TRACKING_MANAGED_BY_THIS_MANAGER',
  'CONVERSION_TRACKING_MANAGED_BY_ANOTHER_MANAGER',
];

const TrackingIssues: React.FC<TrackingIssuesProps> = ({ setStepValid, setIsWarningErrorSuccess }) => {
  const googleAdsAccount = useAtomValue(googleAdsAccountAtom);
  const [minLoadingTime, setMinLoadingTime] = useState(true);

  const {
    isLoading,
    data: conversionTracking,
    refetch: refetchConversionTracking,
  } = useQuery({ ...conversionTrackingQueryOptions(), enabled: !!googleAdsAccount?.googleads_account_id });

  // Ensure loading shows for minimum 1.5 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setMinLoadingTime(false);
    }, 1500);

    return () => clearTimeout(timer);
  }, []);

  const handleTrackingCheck = useCallback(async () => {
    await refetchConversionTracking();
    // Just refetch the data, no toasts
  }, [refetchConversionTracking]);

  const getTrackingIssueDetails = (status: string) => {
    switch (status) {
      case 'CONVERSION_TRACKING_NOT_ENABLED':
        return {
          title: 'Conversion tracking not enabled',
          description: 'Your Google Ads account doesn\'t have conversion tracking set up. You need to configure Google Analytics or Google Tag conversion tracking to measure campaign performance. **Adding conversion tracking is not required** but may impact campaign performance if not set up correctly. You can also add it later if needed.',
          link: 'https://support.google.com/google-ads/answer/1722054',
          severity: 'warning' as const,
        };
      case 'CONVERSION_TRACKING_MANAGED_BY_OTHER_MANAGER':
        return {
          title: 'Conversion tracking managed by different account',
          description: 'Your conversion tracking is managed by a different Google Ads manager account. Please contact your account manager or switch to the correct manager account. **Adding conversion tracking is not required** but may impact campaign performance if not set up correctly. You can also add it later if needed.',
          link: 'https://support.google.com/google-ads/answer/7365001',
          severity: 'warning' as const,
        };
      case 'UNKNOWN':
      case 'UNSPECIFIED':
        return {
          title: 'Unable to verify conversion tracking status',
          description: 'We couldn\'t determine your conversion tracking setup. This might be due to account permissions or a temporary API issue. Please check your Google Ads account or try again later. **Adding conversion tracking is not required** but may impact campaign performance if not set up correctly. You can also add it later if needed.',
          link: 'https://support.google.com/google-ads/answer/1722054',
          severity: 'warning' as const,
        };
      case 'GOOGLE_ANALYTICS_NOT_LINKED':
        return {
          title: 'Google Analytics not properly linked',
          description: 'Your Google Ads account appears to have conversion tracking enabled, but Google Analytics is not properly linked or configured. Please verify your Google Analytics integration. **Adding conversion tracking is not required** but may impact campaign performance if not set up correctly. You can also add it later if needed.',
          link: 'https://support.google.com/analytics/answer/9379420',
          severity: 'warning' as const,
        };
      case 'PERMISSION_ERROR':
        return {
          title: 'Unable to access conversion tracking settings',
          description: 'We don\'t have permission to check your conversion tracking settings. Please ensure GlitchAds has the necessary permissions to access your Google Ads account. **Adding conversion tracking is not required** but may impact campaign performance if not set up correctly. You can also add it later if needed.',
          link: 'https://support.google.com/google-ads/answer/117474',
          severity: 'warning' as const,
        };
      default:
        return {
          title: 'Google Analytics Tracking',
          description: 'Link your Google Analytics account to track your campaign performance and conversions. **Adding conversion tracking is not required** but may impact campaign performance if not set up correctly. You can also add it later if needed.',
          link: 'https://support.google.com/analytics/answer/9379420?hl=en#zippy=%2Cin-this-article',
          severity: 'warning' as const,
        };
    }
  };

  const currentStatus = conversionTracking?.tracking_status || 'UNKNOWN';
  const trackingDetails = getTrackingIssueDetails(currentStatus);
  const allSetup = conversionTracking?.tracking_status
    ? CONVERSION_TRACKING_MANAGED_STATUS.includes(conversionTracking.tracking_status)
    : false;

  // Always allow progression - conversion tracking is not a blocker
  useEffect(() => {
    setStepValid(true);
  }, []);

  // Always show warning/success cards (remove padding)
  useEffect(() => {
    setIsWarningErrorSuccess(true);
  }, [setIsWarningErrorSuccess]);

  // Show loading state while checking setup or during minimum loading time
  if ((isLoading && !conversionTracking) || minLoadingTime) {
    return (
      <motion.div 
        className='campaign-goal-select flex-row space-y-6 !mt-0'
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: "easeInOut" }}
      >
        <motion.div 
          className="flex flex-col items-center justify-center py-16"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
        >
          <Spinner size="xl" className="text-indigo-600 mb-4" />
          <p className="text-lg font-medium text-gray-900">Checking setup</p>
          <p className="text-sm text-gray-500 mt-1">Verifying your tracking configuration...</p>
        </motion.div>
      </motion.div>
    );
  }

  // Show success screen if tracking is properly set up
  if (allSetup) {
    return (
      <motion.div 
        className='campaign-goal-select flex-row space-y-6 !mt-0'
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: "easeInOut" }}
      >
        <motion.div 
          className='bg-green-50 border-l-4 border-l-green-500 rounded-lg p-6'
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
        >
          <div className='space-y-6'>
            <div className='flex items-start space-x-4'>
              <motion.div 
                className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-lg shadow-sm"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: 0.2 }}
              >
                <CheckCircle className="h-8 w-8 text-green-500" />
              </motion.div>
              <motion.div 
                className='flex-1 space-y-4'
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.3 }}
              >
                <div>
                  <h2 className="text-xl font-semibold text-slate-900 mb-1">Conversion tracking is enabled</h2>
                  <p className="text-base text-slate-900 leading-relaxed">
                    Great! Your Google Ads account has conversion tracking properly configured. This will help you measure campaign performance and track conversions effectively.
                  </p>
                </div>
                <div className='flex justify-end'>
                  <a
                    href="https://support.google.com/google-ads/answer/1722054"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-[#290BD9] hover:text-[#2309C5] font-medium transition-colors duration-200"
                  >
                    More information
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </div>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    );
  }

  // Show warning screen if tracking has issues
  return (
    <motion.div 
      className='campaign-goal-select flex-row space-y-6 !mt-0'
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: "easeInOut" }}
    >
      <motion.div 
        className='bg-amber-50 border-l-4 border-l-amber-400 rounded-lg p-6'
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
      >
        <div className='space-y-6'>
          <div className='flex items-start space-x-4'>
            <motion.div 
              className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-lg shadow-sm"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3, delay: 0.2 }}
            >
              <AlertCircle className="h-8 w-8" style={{ color: '#f59e0b' }} />
            </motion.div>
            <motion.div 
              className='flex-1 space-y-4'
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.3 }}
            >
              <div>
                <h2 className="text-xl font-semibold text-slate-900 mb-1">{trackingDetails.title}</h2>
                <p className="text-sm text-gray-500 mb-3">Status: {currentStatus}</p>
                <p className="text-base text-slate-900 leading-relaxed">
                  {trackingDetails.description.includes('**Adding conversion tracking is not required**') ? (
                    <>
                      {trackingDetails.description.split('**Adding conversion tracking is not required**')[0]}
                      <strong>Adding conversion tracking is not required</strong>
                      {trackingDetails.description.split('**Adding conversion tracking is not required**')[1]}
                    </>
                  ) : (
                    trackingDetails.description
                  )}
                </p>
              </div>
              <div className='flex justify-between items-center'>
                <button
                  onClick={handleTrackingCheck}
                  className="inline-flex items-center gap-2 text-[#290BD9] hover:text-[#2309C5] font-medium transition-colors duration-200"
                >
                  Check again
                </button>
                <div className='flex space-x-3'>
                  <a
                    href={trackingDetails.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-[#290BD9] hover:text-[#2309C5] font-medium transition-colors duration-200"
                  >
                    More information
                    <ExternalLink className="h-4 w-4" />
                  </a>
                  <a
                    href="https://docs.google.com/forms/d/e/1FAIpQLSeIazHAtnYveNmfziDjEbOqij1U2YGBRjNzuY24e-p4kIUCDA/viewform"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-[#290BD9] hover:text-[#2309C5] font-medium transition-colors duration-200"
                  >
                    Get help
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default TrackingIssues;